import { useState, useEffect, useCallback, useRef } from "react";
import { AuthContext } from "./auth-context"
import { reservationLogin, validateSession } from "../actions/reservation-auth-actions";
import { getStoredUser, removeAccessToken } from "../util/storage-helpers";

// Session validation key - persists across page refreshes within the same browser session
const SESSION_VALIDATED_KEY = "session_validated";
const SESSION_VALIDATED_TIMESTAMP_KEY = "session_validated_at";
const VALIDATION_TTL_MS = 5 * 60 * 1000; // 5 minutes - don't re-validate within this window

const hasRecentlyValidated = (): boolean => {
    const validated = sessionStorage.getItem(SESSION_VALIDATED_KEY);
    const validatedAt = sessionStorage.getItem(SESSION_VALIDATED_TIMESTAMP_KEY);

    if (validated === "true" && validatedAt) {
        const timestamp = parseInt(validatedAt, 10);
        const now = Date.now();
        const elapsed = now - timestamp;
        if (elapsed < VALIDATION_TTL_MS) {
            return true;
        }
    }
    return false;
};

const markAsValidated = () => {
    sessionStorage.setItem(SESSION_VALIDATED_KEY, "true");
    sessionStorage.setItem(SESSION_VALIDATED_TIMESTAMP_KEY, Date.now().toString());
};

const clearValidationState = () => {
    sessionStorage.removeItem(SESSION_VALIDATED_KEY);
    sessionStorage.removeItem(SESSION_VALIDATED_TIMESTAMP_KEY);
};

export const AppContext = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<any>(() => {
        // Initialize from localStorage synchronously to prevent flash
        const storedUser = getStoredUser();

        if (storedUser) {
            // Check if expired
            if (storedUser.expiresAt && new Date(storedUser.expiresAt) < new Date()) {
                removeAccessToken();
                clearValidationState();
                return null;
            }
            return storedUser;
        }
        return null;
    });

    // Track if we've already validated this session (check sessionStorage first)
    const recentlyValidated = hasRecentlyValidated();
    const hasValidated = useRef(recentlyValidated);

    const cleanUp = useCallback(() => {
        setUser(null);
        removeAccessToken();
        clearValidationState();
        hasValidated.current = false;
    }, []);

    const isSessionExpired = useCallback(() => {
        if (!user?.expiresAt) return false;
        return new Date(user.expiresAt) < new Date();
    }, [user?.expiresAt]);

    // Validate session with server only once on mount for reservation auth
    useEffect(() => {
        const validateOnce = async () => {
            const storedUser = getStoredUser();

            // Only validate if:
            // 1. We have a stored user
            // 2. It's reservation-based auth
            // 3. We haven't validated yet
            if (storedUser && storedUser.authType === "reservation" && !hasValidated.current) {
                hasValidated.current = true;

                try {
                    const response = await validateSession();

                    // Error codes that should NOT cause logout - these are valid sessions with restrictions
                    const nonLogoutErrorCodes = ['reservation_not_started', 'reservation_ended'];

                    if (!response.data.isValid) {
                        const errorCode = response.data.errorCode;
                        if (errorCode && nonLogoutErrorCodes.includes(errorCode)) {
                            // Session is valid but reservation hasn't started or has ended
                            // Don't log out - the user is still authenticated
                            markAsValidated();
                        } else {
                            // Actual invalid session - log out
                            cleanUp();
                        }
                    } else {
                        // Mark as validated in sessionStorage to persist across refreshes
                        markAsValidated();
                    }
                } catch (error) {
                    // Only clean up if it's an auth error (401/403)
                    // Network errors should not log the user out
                    if (error && typeof error === 'object' && 'response' in error) {
                        const axiosError = error as { response?: { status?: number } };
                        if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
                            cleanUp();
                        }
                    }
                }
            }
        };

        validateOnce();
    }, [cleanUp]);

    // Check session expiry periodically for reservation-based auth
    useEffect(() => {
        if (!user || user.authType !== "reservation") return;

        const checkExpiry = () => {
            if (isSessionExpired()) {
                console.log("Session expired, cleaning up");
                cleanUp();
            }
        };

        // Check every minute
        const interval = setInterval(checkExpiry, 60000);
        return () => clearInterval(interval);
    }, [user, isSessionExpired, cleanUp]);

    return (
        <AuthContext.Provider value={{
            user,
            setUser,
            reservationLogin,
            validateSession,
            cleanUp,
            isSessionExpired
        }}>
            {children}
        </AuthContext.Provider>
    );
}
